import type {
	ReelCustomItem,
	ReelFeedItem,
	ReelItem
} from '~/components/PropertyReelsFeed/types';
import type { MaybeRefOrGetter } from 'vue';

import { isReelCustomItem } from '~/components/PropertyReelsFeed/types';

/** Card do imóvel que saiu do ar, no lugar do vídeo que o usuário pediu. */
export const SHORTS_UNAVAILABLE_ENTRY_ITEM: ReelCustomItem = {
	kind: 'custom',
	reelKey: 'unavailable-entry',
	slot: 'unavailable-entry'
};

/** Fim da recomendação com expansão automática da busca. */
export const SHORTS_AUTOMATIC_EXPANSION_ITEM: ReelCustomItem = {
	kind: 'custom',
	reelKey: 'automatic-expansion',
	slot: 'automatic-expansion'
};

/** Fim da recomendação com as opções de expansão para o usuário escolher. */
export const SHORTS_MANUAL_EXPANSION_ITEM: ReelCustomItem = {
	kind: 'custom',
	reelKey: 'manual-expansion',
	slot: 'manual-expansion'
};

export interface UseShortsFeedCompositionOptions {
	/** Vídeos da lista, na ordem em que o usuário os vê. */
	items: MaybeRefOrGetter<ReelItem[]>;
	/** O imóvel de entrada não está mais disponível. */
	hasUnavailableEntry?: MaybeRefOrGetter<boolean>;
	/** A API de expansão ainda não respondeu qual tela usar. */
	isExpansionDecisionPending?: MaybeRefOrGetter<boolean>;
	isManualExpansionScreen?: MaybeRefOrGetter<boolean>;
	isAutomaticExpansionScreen?: MaybeRefOrGetter<boolean>;
}

/**
 * Dono da composição da tela de shorts: decide quais itens custom entram, em que
 * ponta, e traduz entre o índice da lista de vídeos e o índice de slide.
 *
 * O reprodutor recebe uma lista pronta e trata todo slide igual, o que tira dele
 * a compensação de índice que era fonte de off-by-one.
 */
export function useShortsFeedComposition(
	options: UseShortsFeedCompositionOptions
) {
	const items = () => toValue(options.items);
	const hasUnavailableEntry = () =>
		Boolean(toValue(options.hasUnavailableEntry));

	/**
	 * Itens custom antes do primeiro vídeo: a única diferença entre o índice de
	 * slide e o índice em `items`.
	 */
	const leadingItemCount = computed(() => (hasUnavailableEntry() ? 1 : 0));

	const trailingItem = computed<ReelCustomItem | null>(() => {
		if (toValue(options.isExpansionDecisionPending)) return null;
		if (toValue(options.isManualExpansionScreen)) {
			return SHORTS_MANUAL_EXPANSION_ITEM;
		}
		return toValue(options.isAutomaticExpansionScreen)
			? SHORTS_AUTOMATIC_EXPANSION_ITEM
			: null;
	});

	const feedItems = computed<ReelFeedItem[]>(() => [
		...(hasUnavailableEntry() ? [SHORTS_UNAVAILABLE_ENTRY_ITEM] : []),
		...items(),
		...(trailingItem.value ? [trailingItem.value] : [])
	]);

	/** Índice em `items` no espaço de slides do reprodutor. */
	function toFeedIndex(itemIndex: number): number {
		return itemIndex + leadingItemCount.value;
	}

	/** Índice de slide no espaço de `items`; negativo num item custom inicial. */
	function toItemIndex(feedIndex: number): number {
		return feedIndex - leadingItemCount.value;
	}

	/** O slide é uma tela custom, e não um vídeo. */
	function isCustomItemAt(feedIndex: number): boolean {
		return isReelCustomItem(feedItems.value[feedIndex]);
	}

	function isExpansionItemAt(feedIndex: number): boolean {
		const item = feedItems.value[feedIndex];
		return (
			isReelCustomItem(item) &&
			(item.slot === SHORTS_MANUAL_EXPANSION_ITEM.slot ||
				item.slot === SHORTS_AUTOMATIC_EXPANSION_ITEM.slot)
		);
	}

	return {
		feedItems,
		leadingItemCount,
		toFeedIndex,
		toItemIndex,
		isCustomItemAt,
		isExpansionItemAt
	};
}
